/*
 * Copyright (c) 2019 brightlamp
 */

import {axiosInit} from './axiosSetup'
import {deviceCodeToString} from '../components/user-mappings.mjs'

const userRoles =
[
    'ROLE_USER',
    'ROLE_PORTAL',
    'ROLE_ORG_ADMIN',
    'ROLE_ADMIN'
];

const maxAppLevels =
[
    'MOBILE',
    'PRO'
];

export default
{
    namespaced: true,
    state:
    {
        totalRecords: 0,
        users: [],
        totalPlrTestsForUser: 0,
        totalNpcTestsForUser: 0,
        testProtocolSuiteNames: []
    },
    mutations:
    {
        SET_USERS: (state, payload) =>
        {
            state.users = payload
        },
        SET_TOTAL_RECORDS: (state, payload) =>
        {
            state.totalRecords = payload
        },
        SET_USER: (state, payload) =>
        {
            if ('editedIndex' in payload)
            {
                Object.assign(state.users[payload.editedIndex], payload.data)
            }
            else
            {
                state.users.push(payload.data)
            }
        },
        DELETE_USER: (state, payload) =>
        {
            state.users.splice(payload.deleteIndex, 1)
        },
        CLEAR_USERS: (state) =>
        {
            state.users = []
        },
        SET_PLR_TEST_COUNT_FOR_USER: (state, payload) =>
        {
            state.totalPlrTestsForUser = payload
        },
        SET_NPC_TEST_COUNT_FOR_USER: (state, payload) =>
        {
            state.totalNpcTestsForUser = payload
        },
        SET_TEST_PROTOCOL_SUITE_NAMES: (state, payload) =>
        {
            state.testProtocolSuiteNames = payload
        }
    },
    actions:
    {
        GET_ORG_USERS: async (context, payload) =>
        {
            const { page, itemsPerPage } = payload.pagination;
            const axios = await axiosInit();
            const response = await axios.get(`/org/${payload.orgId}/user`,
            {
                params:
                {
                    name: payload.search,
                    page: page,
                    limit: -1 === itemsPerPage ? 2147483647 : itemsPerPage
                }
            });
            context.commit('SET_TOTAL_RECORDS', response.headers['max-count'] * 1);
            context.commit('SET_USERS', processUserData(response.data))
        },
        GET_USERS: async (context, payload) =>
        {
            let params;
            // If we should get the user data based on the filter
            if (!payload.uIds)
            {
                const { page, itemsPerPage } = payload.pagination;
                params =
                {
                    name: payload.search,
                    page: page,
                    limit: -1 === itemsPerPage ? 2147483647 : itemsPerPage
                }
            }
            else // Else get the user data from the user ID list
            {
                params =
                {
                     uIds: encodeURI(payload.uIds)
                }
            }
            const axios = await axiosInit();
            const response = await axios.get(`/users`, {params});
            context.commit('SET_TOTAL_RECORDS', response.headers['max-count'] * 1);
            context.commit('SET_USERS', processUserData(response.data))
        },
        SET_ORG_USER: async (context, payload) =>
        {
            const axios = await axiosInit();
            const response = await axios.post(`/org/${payload.orgId}/user`, payload.data);
            context.commit('SET_USER', {data: response.data})
        },
        UPDATE_USER: async (context, payload) =>
        {
            const axios = await axiosInit();
            const response = await axios.post(`/user/${payload.data.uId}/settings`, payload.data);
            context.commit('SET_USER', {editedIndex: payload.editedIndex, data: response.data})
        },
        DELETE_USER: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.delete(`/user/${payload.uId}`);
            context.commit('DELETE_USER', payload)
        },
        GET_TEST_COUNT_FOR_USER: async (context, payload) =>
        {
            context.commit('SET_PLR_TEST_COUNT_FOR_USER', '0');
            context.commit('SET_NPC_TEST_COUNT_FOR_USER', '0');
            const axios = await axiosInit();
            const response = await axios.get(`/user/${payload.uId}/total-tests`);
            context.commit('SET_PLR_TEST_COUNT_FOR_USER', response.data.totalPlrTests.toLocaleString())
            context.commit('SET_NPC_TEST_COUNT_FOR_USER', response.data.totalNpcTests.toLocaleString())
        },
        SEND_ORG_USER_EMAIL: async (context, payload) =>
        {
            const axios = await axiosInit();
            await axios.post(`/user/${payload}/email`)
        },
        GET_TEST_PROTOCOL_SUITE_NAMES: async (context) =>
        {
            const axios = await axiosInit();
            const response = await axios.get(`/test-protocol-suite-names`);
            let suites = [];
            suites.push('None');
            for (const suite of response.data)
            {
               suites.push(suite.suite)
            }
            context.commit('SET_TEST_PROTOCOL_SUITE_NAMES', suites)
        }
    }
}

function processUserData(users)
{
    const currentDate = new Date();
    for (const user of users)
    {
        let showAsFreeTrial = false;
        // Format the last access date and time
        let date = new Date(user.accessedDate);
        user.accessedDate = date.toLocaleString();
        // Format the creation date and time
        date = new Date(user.createdAt);
        user.createdAt = date.toLocaleString();
        user.testProtoSuite = user.testProtoSuite ? user.testProtoSuite : 'None';
        user.subscription = {};

        // If the user belongs to an org, show one of the org types as their subscription type
        if (user.orgId)
        {
            user.subscription.subId = user.parentOrgId ? 'Organization' : 'Top Organization';
            user.subscription.expiresDate = 'N/A';
            user.freeTrialEndDate = 'N/A'
        }
        else
        {
            // If the user has an override subscription and it has not expired
            if (user.overrideSubscription && new Date(user.overrideSubscription) > currentDate)
            {
                user.subscription.subId = 'Override';
                // Format the override end date and time
                date = new Date(user.overrideSubscription);
                user.subscription.expiresDate = date.toLocaleString();
                user.freeTrialEndDate = 'N/A'
            }
            // If we have a Stripe subscription
            else if (user.stripeSubscription && user.stripeSubscription.subId)
            {
                user.subscription.subId = user.stripeSubscription.subId;
                if ('active' === user.stripeSubscription.status || 'canceled' === user.stripeSubscription.status)
                {
                    // Format the subscription end date and time
                    date = new Date(user.stripeSubscription.subEndDate);
                    user.subscription.expiresDate = date.toLocaleString();
                    user.freeTrialEndDate = 'N/A'
                }
                else
                {
                    user.subscription.subId = 'N/A';
                    user.subscription.expiresDate = 'N/A';
                    user.freeTrialEndDate = 'N/A'
                }
            }
            // If we have an iTunes subscription
            else if (user.appleSubscription && user.appleSubscription.productId)
            {
                // If the user is subscribed or the subscription expired and the user has no more free trial days left
                if ('1' === user.appleSubscription.subscriberLevel || '1e' === user.appleSubscription.subscriberLevel || '2' === user.appleSubscription.subscriberLevel || '2e' === user.appleSubscription.subscriberLevel || '3' === user.appleSubscription.subscriberLevel || '3e' === user.appleSubscription.subscriberLevel)
                {
                    if ('Reflex.sub.allaccess.monthly2' === user.appleSubscription.productId || 'Reflex.sub.allaccess.monthly.noTrial' === user.appleSubscription.productId)
                    {
                        user.subscription.subId = 'iTunes Monthly'
                    }
                    else if ('Reflex.sub.allaccess.yearly2' === user.appleSubscription.productId || 'Reflex.sub.allaccess.yearly.noTrial' === user.appleSubscription.productId)
                    {
                        user.subscription.subId = 'iTunes Yearly'
                    }
                    else if ('Reflex.sub.liteAccess2.monthly.noTrial' === user.appleSubscription.productId)
                    {
                        user.subscription.subId = 'iTunes Critical Care'
                    }
                    else
                    {
                        user.subscription.subId = 'iTunes Monthly Pro'
                    }
                    // Format the subscription end date and time
                    date = new Date(user.appleSubscription.expiresDate);
                    user.subscription.expiresDate = date.toLocaleString();
                    user.freeTrialEndDate = 'N/A'
                }
                else
                {
                    showAsFreeTrial = true
                }
            }
            else
            {
                showAsFreeTrial = true
            }
            if (showAsFreeTrial)
            {
                user.subscription.subId = 'N/A';
                user.subscription.expiresDate = 'N/A';
                // Format the subscription end date and time
                date = new Date(user.freeTrialEndDate);
                user.freeTrialEndDate = date.toLocaleString()
            }
        }
        // Decode app version, device type, and os version
        if (!user.appInfo)
        {
            user.deviceName = 'N/A';
            user.deviceProName = 'N/A';
            user.osName = 'N/A';
            user.osProName = 'N/A';
            user.appVersion = 'N/A';
            user.appVersion = 'N/A';
            user.appProVersion = 'N/A';
            user.browserInfo = 'N/A'
        }
        else
        {
            user.deviceName = user.appInfo.deviceName && user.appInfo.deviceName !== '' ? deviceCodeToString(user.appInfo.deviceName) : 'N/A';
            user.deviceProName = user.appInfo.deviceProName && user.appInfo.deviceProName !== '' ? deviceCodeToString(user.appInfo.deviceProName) : 'N/A';
            user.osName = user.appInfo.osName && user.appInfo.osName !== '' ? user.appInfo.osName : 'N/A';
            user.osProName = user.appInfo.osProName && user.appInfo.osProName !== '' ? user.appInfo.osProName : 'N/A';
            user.appVersion = user.appInfo.appVersion && user.appInfo.appVersion !== '' ? user.appInfo.appVersion : 'N/A';
            user.appProVersion = user.appInfo.appProVersion && user.appInfo.appProVersion !== '' ? user.appInfo.appProVersion : 'N/A';
            user.browserInfo = user.appInfo.browserInfo && user.appInfo.browserInfo !== '' ? user.appInfo.browserInfo : 'N/A'
        }
    }
    return users
}

export { userRoles, maxAppLevels }